/**
 * Clutch Reel frame layout (vertical 720×1280). Pure geometry + copy, unit-tested;
 * `compose.ts` only paints what this returns.
 */
export const REEL_W = 720;
export const REEL_H = 1280;

export type Rect = { x: number; y: number; w: number; h: number };

/** object-fit: cover — the source crop that fills `box` without distortion. */
export function coverCrop(srcW: number, srcH: number, box: Pick<Rect, 'w' | 'h'>): Rect {
  if (srcW <= 0 || srcH <= 0) return { x: 0, y: 0, w: Math.max(0, srcW), h: Math.max(0, srcH) };
  const target = box.w / box.h;
  const src = srcW / srcH;
  if (src > target) {
    const w = srcH * target;
    return { x: (srcW - w) / 2, y: 0, w, h: srcH };
  }
  const h = srcW / target;
  return { x: 0, y: (srcH - h) / 2, w: srcW, h };
}

export type ReelLayout = {
  w: number;
  h: number;
  /** Fan video panes: one full-bleed, or two stacked for side-by-side. */
  panes: { rect: Rect; label?: string }[];
  scoreBug: Rect;
  lowerThird: Rect;
  headshot: Rect; // circle inscribed in this square, inside the lower third
  wordmark: { x: number; y: number; size: number };
  callChip: Rect;
};

export function reelLayout(mode: 'solo' | 'split', names: { me: string; friend?: string } = { me: 'You' }): ReelLayout {
  const w = REEL_W;
  const h = REEL_H;
  const pad = 32;
  const panes =
    mode === 'solo'
      ? [{ rect: { x: 0, y: 0, w, h } }]
      : [
          { rect: { x: 0, y: 0, w, h: h / 2 - 2 }, label: names.me },
          { rect: { x: 0, y: h / 2 + 2, w, h: h / 2 - 2 }, label: names.friend ?? 'Friend' },
        ];
  const ltH = 132;
  // Solo: lower third sits in the bottom quarter; split: straddles the seam so both faces stay clear.
  const ltY = mode === 'solo' ? h - 300 : h / 2 - ltH / 2;
  return {
    w,
    h,
    panes,
    scoreBug: { x: pad, y: pad + 12, w: w - pad * 2, h: 84 },
    lowerThird: { x: pad, y: ltY, w: w - pad * 2, h: ltH },
    headshot: { x: pad + 8, y: ltY - 34, w: 150, h: 150 },
    wordmark: { x: pad, y: h - 56, size: 40 },
    callChip: { x: pad, y: h - 150, w: w - pad * 2, h: 64 },
  };
}

/** The fan's call line on the reel: "JAY CALLED HAWKS · BEAT A 22% SHOT" (the shot only when correct and < 50%). */
export function callLine(c: { name: string; label: string; correct?: boolean; pAtLock?: number } | undefined): string | undefined {
  if (!c) return undefined;
  const base = `${c.name.toUpperCase()} CALLED ${c.label.toUpperCase()}`;
  const p = c.pAtLock;
  if (c.correct && typeof p === 'number' && Number.isFinite(p) && p > 0 && p < 0.5) return `${base} · BEAT A ${Math.round(p * 100)}% SHOT`;
  if (c.correct) return `${base} · CALLED IT`;
  return base;
}

/** Fit text by shrinking the font: largest size ≤ max whose width (via `measure`) fits. */
export function fitFont(measure: (size: number) => number, maxWidth: number, max: number, min = 14): number {
  for (let s = max; s > min; s -= 2) if (measure(s) <= maxWidth) return s;
  return min;
}
